// src/pages/CreatePost.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import pb from '../lib/pb';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import StickyFooter from "../components/StickyFooter";
import {
  Avatar,
  MAX_PRODUCTS,
  POST_COLORS,
  cardColorVars,
  getMediaItems,
  getProducts,
  injectStyles,
  isHexColor,
  normalizeUrl,
  productImageUrl,
  useMfTheme,
} from '../shared/PostCard';
import { getMediaUrl, getPbUrl } from '../lib/media';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import TextFieldsRoundedIcon from '@mui/icons-material/TextFieldsRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import MovieRoundedIcon from '@mui/icons-material/MovieRounded';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

const CLOUDFLARE_WORKER_URL = "https://social-media-backend.myfavvcompany-internal.workers.dev";
const PB_URL = "https://go.myfavv.live";

const MAX_ITEMS = 10;        // slides per post
const MAX_FILE_MB = 100;     // per file
const MAX_TEXT_SLIDE = 600;  // characters per text slide
const MAX_PRODUCT_MB = 10;   // per product image (images are also shrunk before upload)

// The field in your "brands" collection that points to the user who owns the brand
// (e.g. 'owner'). Leave it empty to auto-detect from a few common names.
const BRAND_OWNER_FIELD = '';
const BRAND_FIELD_GUESSES = ['owner', 'user', 'author', 'userId', 'user_id', 'creator', 'createdBy'];
const BRAND_FIELD_CACHE = 'mf-brand-owner-field';

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */
const CREATE_CSS = `
.mf-cp{--mf-pad:clamp(12px,2.2vw,32px);margin:0;width:100%;min-height:100vh;
  background:radial-gradient(1000px 380px at 50% -140px,var(--mf-glow),transparent 70%),var(--mf-bg);color:var(--mf-text)}

.mf-cp-top{position:sticky;top:0;z-index:50;background:var(--mf-bg-top);border-bottom:1px solid var(--mf-border)}
.mf-cp-top-inner{display:flex;align-items:center;gap:10px;padding:10px var(--mf-pad)}
.mf-cp-title{margin:0;flex:1;min-width:0;font-size:19px;font-weight:800;letter-spacing:-.01em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.mf-cp-top-actions{display:flex;align-items:center;gap:8px}
.mf-cp-iconbtn{width:40px;height:40px;flex:none;border-radius:12px;border:1px solid var(--mf-border);background:var(--mf-surface);color:var(--mf-muted);
  display:grid;place-items:center;cursor:pointer;text-decoration:none;transition:color .15s,border-color .15s,background-color .15s}
.mf-cp-iconbtn:hover{color:var(--mf-text);border-color:var(--mf-border-strong);background:var(--mf-surface-2)}
.mf-cp-iconbtn svg{font-size:22px}

.mf-cp-grid{display:grid;grid-template-columns:minmax(0,1fr);gap:18px;width:100%;padding:18px var(--mf-pad) 130px;align-items:start}
@media (min-width:980px){.mf-cp-grid{grid-template-columns:minmax(0,1fr) 400px}}
.mf-cp-col{display:flex;flex-direction:column;gap:18px;min-width:0}

.mf-cp-card{background:var(--mf-surface);border:1px solid var(--mf-border);border-radius:18px;box-shadow:var(--mf-shadow);padding:16px}
.mf-cp-h{margin:0 0 12px;display:flex;align-items:center;justify-content:space-between;gap:10px;font-size:12.5px;font-weight:800;
  letter-spacing:.09em;text-transform:uppercase;color:var(--mf-faint)}
.mf-cp-h small{font-size:12px;font-weight:600;letter-spacing:0;text-transform:none}
.mf-cp-caption{min-height:132px}
.mf-cp-count{margin-top:6px;text-align:right;font-size:12px;color:var(--mf-faint)}

.mf-cp-notice{padding:11px 14px;border-radius:12px;font-size:14px;font-weight:600;background:rgba(255,107,107,.12);
  border:1px solid rgba(255,107,107,.4);color:#ff8a8a}
.mf-feed[data-mf-theme="light"] .mf-cp-notice{color:#c0392b}
.mf-cp-state{margin:60px auto;max-width:420px;text-align:center;color:var(--mf-muted);padding:0 20px}

/* Drop zone */
.mf-cp-drop{display:flex;flex-direction:column;align-items:center;gap:10px;text-align:center;padding:22px 16px;border:2px dashed var(--mf-border-strong);
  border-radius:16px;background:var(--mf-surface-2);color:var(--mf-muted);transition:border-color .15s,background-color .15s}
.mf-cp-drop.is-over{border-color:var(--mf-accent);background:var(--mf-accent-soft)}
.mf-cp-drop-ico{width:48px;height:48px;border-radius:14px;display:grid;place-items:center;background:var(--mf-accent-soft);color:var(--mf-accent)}
.mf-cp-drop-ico svg{font-size:26px}
.mf-cp-drop p{margin:0;font-size:14px}
.mf-cp-drop small{color:var(--mf-faint);font-size:12.5px}
.mf-cp-drop-actions{display:flex;flex-wrap:wrap;gap:8px;justify-content:center}

.mf-cp-textbox{margin-top:12px;display:flex;flex-direction:column;gap:8px}
.mf-cp-textbox-row{display:flex;justify-content:flex-end;gap:8px}

/* Tiles */
.mf-cp-tiles{list-style:none;margin:14px 0 0;padding:0;display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:12px}
.mf-cp-tile{position:relative;border-radius:14px;overflow:hidden;background:var(--mf-surface-2);border:1px solid var(--mf-border);aspect-ratio:1/1}
.mf-cp-tile.is-cover{border-color:var(--mf-accent);box-shadow:0 0 0 1px var(--mf-accent)}
.mf-cp-thumb{position:absolute;inset:0;display:grid;place-items:center;background:var(--mf-surface-3);overflow:hidden}
.mf-cp-thumb img,.mf-cp-thumb video{width:100%;height:100%;object-fit:cover;display:block}
.mf-cp-thumb-text{width:100%;padding:14px;font-size:13px;font-weight:600;line-height:1.4;overflow:hidden;display:-webkit-box;-webkit-line-clamp:6;
  -webkit-box-orient:vertical;overflow-wrap:anywhere;text-align:center;color:var(--mf-text)}
.mf-cp-thumb-text.is-large{font-size:clamp(16px,2.4vw,21px);-webkit-line-clamp:7}
.mf-cp-thumb-music{display:grid;gap:6px;place-items:center;padding:10px;text-align:center;color:var(--mf-muted);font-size:12px;font-weight:600;max-width:100%}
.mf-cp-thumb-music span{max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:block}
.mf-cp-thumb-music svg{font-size:36px;color:var(--mf-accent)}
.mf-cp-num{position:absolute;top:8px;left:8px;z-index:2;min-width:24px;height:24px;padding:0 7px;border-radius:999px;display:grid;place-items:center;
  background:rgba(0,0,0,.66);color:#fff;font-size:12px;font-weight:800}
.mf-cp-cover{position:absolute;top:8px;right:8px;z-index:2;padding:3px 9px;border-radius:999px;background:var(--mf-grad);color:#fff;font-size:11px;
  font-weight:800;letter-spacing:.04em}
.mf-cp-type{position:absolute;left:8px;bottom:46px;z-index:2;width:24px;height:24px;border-radius:8px;display:grid;place-items:center;
  background:rgba(0,0,0,.62);color:#fff}
.mf-cp-type svg{font-size:15px}
.mf-cp-tools{position:absolute;left:0;right:0;bottom:0;z-index:2;display:flex;justify-content:space-between;gap:4px;padding:8px 6px 6px;
  background:linear-gradient(to top,rgba(0,0,0,.74),rgba(0,0,0,0))}
.mf-cp-tools button{width:30px;height:30px;border:0;border-radius:9px;background:rgba(0,0,0,.58);color:#fff;display:grid;place-items:center;cursor:pointer;
  transition:background-color .15s}
.mf-cp-tools button:hover:not(:disabled){background:rgba(0,0,0,.85)}
.mf-cp-tools button:disabled{opacity:.35;cursor:default}
.mf-cp-tools button.is-on{color:#ffd166}
.mf-cp-tools svg{font-size:18px}
.mf-cp-tip{margin:12px 0 0;font-size:12.5px;color:var(--mf-faint)}

/* Shop links (products / services + brand website) */
.mf-cp-hint{margin:0 0 14px;font-size:13px;color:var(--mf-muted)}
.mf-cp-label{display:block;margin:0 0 6px;font-size:13px;font-weight:700;color:var(--mf-text)}
.mf-cp-field{position:relative;display:flex;align-items:center}
.mf-cp-field>svg{position:absolute;left:12px;font-size:20px;color:var(--mf-faint);pointer-events:none}
.mf-cp-field .mf-input{width:100%;border-radius:12px;padding-left:40px}
.mf-cp-fieldhint{margin:6px 0 0;font-size:12.5px;color:var(--mf-faint)}
.mf-cp-fieldhint.is-good{color:var(--mf-success)}
.mf-cp-shop{list-style:none;margin:16px 0 0;padding:0;display:flex;flex-direction:column;gap:12px}
.mf-cp-prod{display:grid;grid-template-columns:76px minmax(0,1fr);gap:12px;padding:12px;border-radius:16px;background:var(--mf-surface-2);
  border:1px solid var(--mf-border)}
.mf-cp-prodimg{position:relative;width:76px;height:76px;padding:0;border:0;border-radius:18px;overflow:hidden;cursor:pointer;
  background:var(--mf-surface-3);display:grid;place-items:center;color:var(--mf-faint)}
.mf-cp-prodimg img{width:100%;height:100%;object-fit:cover;display:block}
.mf-cp-prodimg i{position:absolute;left:6px;top:6px;min-width:20px;height:20px;padding:0 6px;border-radius:999px;background:rgba(0,0,0,.66);color:#fff;
  font-size:11px;font-weight:800;font-style:normal;display:grid;place-items:center}
.mf-cp-prodimg span{position:absolute;left:0;right:0;bottom:0;padding:3px 0;font-size:10.5px;font-weight:800;text-align:center;color:#fff;background:rgba(0,0,0,.6)}
.mf-cp-prodfields{display:flex;flex-direction:column;gap:8px;min-width:0}
.mf-cp-prodfields .mf-input{width:100%;height:40px;border-radius:12px;padding:0 12px;font-size:15px}
.mf-cp-prodtools{display:flex;align-items:center;gap:6px}
.mf-cp-prodtools .mf-cp-iconbtn{width:34px;height:34px;border-radius:10px}
.mf-cp-prodtools .mf-cp-iconbtn svg{font-size:20px}
.mf-cp-prodtools .mf-cp-iconbtn:disabled{opacity:.35;cursor:default}
.mf-cp-prodtools .is-danger:hover{color:#ff6b6b;border-color:rgba(255,107,107,.5)}
.mf-cp-empty{display:flex;align-items:center;gap:10px;margin:16px 0 0;padding:14px;border-radius:14px;border:1px dashed var(--mf-border-strong);
  color:var(--mf-muted);font-size:13.5px;background:var(--mf-surface-2)}
.mf-cp-empty svg{font-size:22px;color:var(--mf-accent);flex:none}
.mf-cp-addprod{margin-top:14px}

/* Visibility */
.mf-cp-seg{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.mf-cp-seg button{display:flex;align-items:center;gap:10px;text-align:left;padding:12px;border-radius:14px;border:1px solid var(--mf-border-strong);
  background:var(--mf-surface-2);color:var(--mf-muted);cursor:pointer;font:inherit;transition:border-color .15s,background-color .15s}
.mf-cp-seg button.is-on{border-color:var(--mf-accent);background:var(--mf-accent-soft);color:var(--mf-text)}
.mf-cp-seg b{display:block;font-size:14px}
.mf-cp-seg small{display:block;font-size:12px;color:var(--mf-faint);line-height:1.3}
.mf-cp-seg svg{font-size:22px;flex:none}

/* Palette */
.mf-cp-swatches{display:flex;flex-wrap:wrap;gap:10px}
.mf-cp-sw{position:relative;width:34px;height:34px;padding:0;border-radius:50%;border:2px solid var(--mf-border-strong);cursor:pointer;display:grid;
  place-items:center;transition:transform .12s;overflow:hidden}
.mf-cp-sw:hover{transform:scale(1.08)}
.mf-cp-sw.is-on{box-shadow:0 0 0 2px var(--mf-surface),0 0 0 4px var(--mf-accent)}
.mf-cp-sw svg{font-size:18px;filter:drop-shadow(0 1px 2px rgba(0,0,0,.45))}
.mf-cp-sw--default{background:linear-gradient(135deg,var(--mf-surface) 50%,var(--mf-surface-3) 50%)}
.mf-cp-sw--default svg{color:var(--mf-text);filter:none}
.mf-cp-sw--custom{background:conic-gradient(#ef4444,#f59e0b,#84cc16,#10b981,#06b6d4,#3b82f6,#8b5cf6,#ec4899,#ef4444)}
.mf-cp-sw--custom input{position:absolute;inset:0;width:100%;height:100%;opacity:0;cursor:pointer;border:0;padding:0}
.mf-cp-colorname{margin:10px 0 0;font-size:13px;color:var(--mf-muted)}

/* Preview */
.mf-cp-pv{border-radius:16px;border:1px solid var(--mf-border);background:var(--mf-surface);padding:12px;display:flex;flex-direction:column;gap:10px;
  color:var(--mf-text);transition:background-color .2s,color .2s}
.mf-cp-pv-head{display:flex;align-items:center;gap:10px;min-width:0}
.mf-cp-pv-head b{display:block;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.mf-cp-pv-head small{display:block;color:var(--mf-faint);font-size:12px}
.mf-cp-pv-media{position:relative;aspect-ratio:4/3;border-radius:12px;overflow:hidden;background:var(--mf-surface-2)}
.mf-cp-pv-media .mf-cp-thumb{background:transparent}
.mf-cp-pv-media .mf-cp-thumb img,.mf-cp-pv-media .mf-cp-thumb video{object-fit:contain}
.mf-cp-pv-count{position:absolute;top:8px;right:8px;z-index:2;padding:3px 9px;border-radius:999px;background:rgba(0,0,0,.65);color:#fff;font-size:12px;font-weight:800}
.mf-cp-pv-text{margin:0;font-size:14.5px;line-height:1.5;white-space:pre-wrap;overflow-wrap:anywhere;display:-webkit-box;-webkit-line-clamp:5;-webkit-box-orient:vertical;overflow:hidden}
.mf-cp-pv-text.is-empty{color:var(--mf-faint)}
.mf-cp-pv-actions{display:flex;gap:16px;font-size:13px;color:var(--mf-muted);padding-top:8px;border-top:1px solid var(--mf-border)}

/* Publish */
.mf-cp-progress{height:8px;border-radius:999px;background:var(--mf-surface-3);overflow:hidden}
.mf-cp-progress span{display:block;height:100%;background:var(--mf-grad);transition:width .25s}
.mf-cp-publish{display:flex;flex-direction:column;gap:10px}
.mf-cp-publish .mf-btn{height:46px;font-size:15px;border-radius:14px}
.mf-btn--danger{color:#ff6b6b;border-color:rgba(255,107,107,.45);background:transparent}
.mf-btn--danger:hover{background:rgba(255,107,107,.12)}

@media (max-width:560px){
  .mf-cp-tiles{grid-template-columns:repeat(2,minmax(0,1fr))}
  .mf-cp-seg{grid-template-columns:1fr}
  .mf-cp-prod{grid-template-columns:64px minmax(0,1fr);gap:10px;padding:10px}
  .mf-cp-prodimg{width:64px;height:64px;border-radius:16px}
}
@media (prefers-reduced-motion:reduce){
  .mf-cp *{transition:none!important}
}
`;
injectStyles('mf-create-css', CREATE_CSS);

/* ------------------------------------------------------------------ */
/* Crypto helpers                                                      */
/* ------------------------------------------------------------------ */
function genKey() {
  const arr = window.crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...arr));
}

async function encryptText(base64Key, text) {
  const keyBytes = Uint8Array.from(atob(base64Key), c => c.charCodeAt(0));
  const cryptoKey = await window.crypto.subtle.importKey('raw', keyBytes, 'AES-GCM', true, ['encrypt']);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder().encode(text);
  const ct = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, cryptoKey, enc);
  const combined = new Uint8Array(iv.byteLength + ct.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ct), iv.byteLength);
  return btoa(String.fromCharCode(...combined));
}

async function decryptText(base64Key, base64Data) {
  const keyBytes = Uint8Array.from(atob(base64Key), c => c.charCodeAt(0));
  const dataBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
  const iv = dataBytes.slice(0, 12);
  const ct = dataBytes.slice(12);
  const key = await window.crypto.subtle.importKey('raw', keyBytes, 'AES-GCM', true, ['decrypt']);
  const buf = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
  return new TextDecoder().decode(buf);
}

/* ------------------------------------------------------------------ */
/* Upload helpers                                                      */
/* ------------------------------------------------------------------ */

// A valid stored filename looks like "f57a3581-....jpeg": no slashes, braces or spaces
const VALID_FILENAME = /^[A-Za-z0-9][A-Za-z0-9._-]*\.[A-Za-z0-9]{2,5}$/;

function extractFilename(result) {
  const candidates = [result.fileName, result.filename, result.key, result.mediaKey, result.mediaUrl];
  for (const c of candidates) {
    if (typeof c !== 'string' || !c) continue;
    const last = c.split('?')[0].split('#')[0].split('/').pop();
    if (VALID_FILENAME.test(last)) return last;
  }
  return null;
}

// folder: 'posts' (default) or 'products' (shop images)
async function uploadToWorker(file, content, username, folder = 'posts') {
  const fd = new FormData();
  fd.append('content', content);
  fd.append('file', file);
  if (folder !== 'posts') fd.append('folder', folder);

  const res = await fetch(`${CLOUDFLARE_WORKER_URL}/api/post`, {
    method: 'POST',
    headers: { 'X-User-Name': username || 'anonymous' },
    body: fd,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(`Uploading “${file.name}” failed: ${(body && body.error) || `status ${res.status}`}.`);
  }

  const result = await res.json();
  if (!result.success) throw new Error(result.error || `Uploading “${file.name}” failed.`);

  // an old Worker ignores "folder" and would save the image in the wrong place
  if (folder !== 'posts' && result.folder !== folder) {
    throw new Error(`The upload Worker needs the latest update (it did not save to the “${folder}” folder). Redeploy it and try again.`);
  }

  const filename = extractFilename(result);
  if (!filename) throw new Error(`“${file.name}” uploaded, but the Worker returned an invalid filename.`);
  return filename;
}

function detectType(file) {
  const t = (file.type || '').toLowerCase();
  if (t.startsWith('image/')) return 'image';
  if (t.startsWith('video/')) return 'video';
  if (t.startsWith('audio/') || /\.(mp3|wav|ogg|aac|flac|m4a)$/i.test(file.name)) return 'music';
  if (/\.(jpe?g|png|gif|webp|avif|svg)$/i.test(file.name)) return 'image';
  if (/\.(mp4|webm|mov|mkv|avi)$/i.test(file.name)) return 'video';
  return null;
}

// Product images are shown tiny, so shrink big photos before uploading (falls back to the original)
async function shrinkImage(file, maxSide = 720) {
  try {
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type) || typeof createImageBitmap !== 'function') return file;
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
    if (scale === 1 && file.size < 400 * 1024) {
      if (bmp.close) bmp.close();
      return file;
    }
    const w = Math.max(1, Math.round(bmp.width * scale));
    const h = Math.max(1, Math.round(bmp.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    const isPng = file.type === 'image/png';
    if (!isPng) { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h); }
    ctx.drawImage(bmp, 0, 0, w, h);
    if (bmp.close) bmp.close();
    const type = isPng ? 'image/png' : 'image/jpeg';
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, type, 0.86));
    if (!blob || blob.size >= file.size) return file;
    const base = file.name.replace(/\.[^.]+$/, '') || 'product';
    return new File([blob], `${base}.${isPng ? 'png' : 'jpg'}`, { type });
  } catch {
    return file;
  }
}

// Looks up the website saved on the user's brand (PocketBase "brands" collection, field "website")
async function fetchBrandWebsite(userId) {
  let cached = '';
  try { cached = localStorage.getItem(BRAND_FIELD_CACHE) || ''; } catch {}
  const fields = BRAND_OWNER_FIELD
    ? [BRAND_OWNER_FIELD]
    : (cached ? [cached] : BRAND_FIELD_GUESSES);

  for (const f of fields) {
    try {
      const rec = await pb.collection('brands').getFirstListItem(`${f}="${userId}"`, { $autoCancel: false });
      try { localStorage.setItem(BRAND_FIELD_CACHE, f); } catch {}
      return { status: 'found', website: String(rec.website || '').trim() };
    } catch (err) {
      if (err && err.status === 400) continue;   // that field doesn't exist in "brands": try the next guess
      return { status: 'none', website: '' };    // no brand for this user (or no access)
    }
  }

  // maybe the brand record uses the user's own id
  try {
    const rec = await pb.collection('brands').getOne(userId, { $autoCancel: false });
    return { status: 'found', website: String(rec.website || '').trim() };
  } catch {
    return { status: 'none', website: '' };
  }
}

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

// Enter inside a single-line field should not publish the post
const noEnterSubmit = (e) => { if (e.key === 'Enter') e.preventDefault(); };

const TYPE_ICON = {
  image: ImageRoundedIcon,
  video: MovieRoundedIcon,
  music: MusicNoteRoundedIcon,
  text: TextFieldsRoundedIcon,
};
const TYPE_LABEL = { image: 'Image', video: 'Video', music: 'Audio', text: 'Text' };

/* ------------------------------------------------------------------ */
/* Small components                                                    */
/* ------------------------------------------------------------------ */

// Preview of one item (local file, already-uploaded file, or text slide)
function Thumb({ item, record, large }) {
  const [fallback, setFallback] = useState(false);

  useEffect(() => { setFallback(false); }, [item.name, item.previewUrl]);

  if (item.type === 'text') {
    return <div className={`mf-cp-thumb-text${large ? ' is-large' : ''}`}>{item.text}</div>;
  }

  let src = null;
  if (item.kind === 'file') src = item.previewUrl;
  else if (fallback) src = record ? getPbUrl(record, item.name) : null;
  else src = getMediaUrl(record || {}, item.name);

  if (item.type === 'music') {
    return (
      <div className="mf-cp-thumb-music">
        <MusicNoteRoundedIcon />
        <span>{item.kind === 'file' ? item.name : 'Audio'}</span>
      </div>
    );
  }
  if (!src) return <ImageRoundedIcon style={{ opacity: 0.4 }} />;

  if (item.type === 'video') {
    return (
      <video
        src={`${src}#t=0.1`}
        muted
        playsInline
        preload="metadata"
        onError={() => { if (!fallback) setFallback(true); }}
      />
    );
  }
  return <img src={src} alt="" draggable={false} onError={() => { if (!fallback) setFallback(true); }} />;
}

// A product row's picture (local file preview, or the image already stored in R2)
function ProductImg({ p }) {
  const [bad, setBad] = useState(false);

  useEffect(() => { setBad(false); }, [p.previewUrl, p.image]);

  const src = p.kind === 'file' ? p.previewUrl : productImageUrl(p.image);
  if (!src || bad) return <ShoppingBagRoundedIcon style={{ opacity: 0.5 }} />;
  return <img src={src} alt="" draggable={false} onError={() => setBad(true)} />;
}

// Mini version of how the card will look, using the chosen background color
function PostPreview({ items, caption, bgColor, license, user, editRecord, shopCount, hasSite }) {
  const vars = cardColorVars(bgColor);
  const cover = items[0];
  const name = (user?.name && user.name.trim()) || user?.username || 'You';
  const text = caption.trim();

  return (
    <div className="mf-cp-pv" style={vars}>
      <div className="mf-cp-pv-head">
        <Avatar user={user} size={34} />
        <div style={{ minWidth: 0 }}>
          <b>{name}</b>
          <small>just now · {license}</small>
        </div>
      </div>

      {cover && (
        <div className="mf-cp-pv-media">
          <div className="mf-cp-thumb"><Thumb item={cover} record={editRecord} large /></div>
          {items.length > 1 && <span className="mf-cp-pv-count">1/{items.length}</span>}
        </div>
      )}

      <p className={`mf-cp-pv-text${text ? '' : ' is-empty'}`}>
        {text || 'Your caption will appear here…'}
      </p>

      <div className="mf-cp-pv-actions" aria-hidden="true">
        <span>♡ 0</span>
        <span>🔁 0</span>
        <span>💬 0</span>
        {shopCount > 0 && <span>🛍 {shopCount}</span>}
        {hasSite && <span>🌐</span>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function CreatePost() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get('edit');          // /create?edit=<postId>
  const [theme, toggleTheme] = useMfTheme();

  const [caption, setCaption] = useState('');
  const [license, setLicense] = useState('Public');
  const [bgColor, setBgColor] = useState('');   // '' = theme default
  const [items, setItems] = useState([]);
  const [showTextBox, setShowTextBox] = useState(false);
  const [textDraft, setTextDraft] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null);   // { done, total }

  // shop: products / services (max 4) + the brand website
  const [shop, setShop] = useState([]);
  const [website, setWebsite] = useState('');
  const [brand, setBrand] = useState({ status: 'idle', website: '' });  // idle | loading | found | none

  const [loadingEdit, setLoadingEdit] = useState(!!editId);
  const [editRecord, setEditRecord] = useState(null);
  const [editError, setEditError] = useState('');
  const isEdit = !!editId && !!editRecord;

  const fileInputRef = useRef(null);
  const prodFileRef = useRef(null);
  const pickingSlot = useRef(null);            // 'new' or the id of the product row whose image is being replaced
  const websiteTouched = useRef(false);        // once the user (or the post) set the website, never overwrite it
  const itemsRef = useRef([]);
  const shopRef = useRef([]);

  // keep the lists in refs too, so handlers always see the latest values
  const setList = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(itemsRef.current) : updater;
    itemsRef.current = next;
    setItems(next);
  }, []);

  const setShopList = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(shopRef.current) : updater;
    shopRef.current = next;
    setShop(next);
  }, []);

  // free local preview URLs when leaving the page
  useEffect(() => () => {
    itemsRef.current.forEach((i) => { if (i.previewUrl) URL.revokeObjectURL(i.previewUrl); });
    shopRef.current.forEach((p) => { if (p.previewUrl) URL.revokeObjectURL(p.previewUrl); });
  }, []);

  /* ---------- Brand website: fill it in from the user's brand profile ---------- */
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    setBrand({ status: 'loading', website: '' });
    fetchBrandWebsite(user.id).then((res) => {
      if (cancelled) return;
      setBrand(res);
      if (res.website && !websiteTouched.current) setWebsite((cur) => cur || res.website);
    });
    return () => { cancelled = true; };
  }, [user?.id]);

  /* ---------- Load an existing post (edit mode) ---------- */
  useEffect(() => {
    if (!editId) return undefined;
    if (!user?.id) { setLoadingEdit(false); return undefined; }

    let cancelled = false;
    (async () => {
      setLoadingEdit(true);
      setEditError('');
      try {
        const rec = await pb.collection('posts').getOne(editId, { $autoCancel: false });
        if (cancelled) return;

        if (rec.author !== user.id) {
          setEditError('You can only edit your own posts.');
          return;
        }

        let cap = rec.caption || '';
        const priv = rec.licenseType === 'Private';
        if (priv) {
          cap = '';
          if (rec.encryptedContent && rec.privateKey) {
            try { cap = await decryptText(rec.privateKey, rec.encryptedContent); } catch { cap = ''; }
          }
        } else {
          cap = rec.caption || (rec.content && rec.content !== '[private]' ? rec.content : '') || '';
        }
        if (cancelled) return;

        setEditRecord(rec);
        setCaption(cap);
        setLicense(priv ? 'Private' : 'Public');
        setBgColor(isHexColor(rec.bgColor) ? rec.bgColor.trim().toLowerCase() : '');
        setList(
          getMediaItems(rec).map((it) =>
            it.type === 'text'
              ? { id: uid(), kind: 'text', type: 'text', text: it.text }
              : { id: uid(), kind: 'existing', type: it.type, name: it.name }
          )
        );

        // shop links saved with this post
        setShopList(
          getProducts(rec).map((p) => ({
            id: uid(), kind: 'existing', image: p.image, url: p.url, title: p.title || '',
          }))
        );
        const siteFromPost = normalizeUrl(rec.brandWebsite);
        if (siteFromPost) {
          websiteTouched.current = true;
          setWebsite(siteFromPost);
        }
      } catch (err) {
        console.error('Load post failed', err);
        if (!cancelled) setEditError('Could not load this post. It may have been deleted.');
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();

    return () => { cancelled = true; };
  }, [editId, user?.id, setList, setShopList]);

  /* ---------- Items: add / remove / reorder / cover ---------- */
  const addFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const added = [];
    const problems = [];
    let count = itemsRef.current.length;

    for (const f of files) {
      if (count >= MAX_ITEMS) { problems.push(`You can add up to ${MAX_ITEMS} items per post.`); break; }
      const type = detectType(f);
      if (!type) { problems.push(`“${f.name}” isn’t a supported image, video or audio file.`); continue; }
      if (f.size > MAX_FILE_MB * 1024 * 1024) { problems.push(`“${f.name}” is larger than ${MAX_FILE_MB} MB.`); continue; }
      added.push({
        id: uid(),
        kind: 'file',
        type,
        file: f,
        name: f.name,
        previewUrl: URL.createObjectURL(f),
      });
      count += 1;
    }

    if (added.length) setList((prev) => [...prev, ...added]);
    setNotice(problems.join(' '));
  }, [setList]);

  const removeItem = (id) => {
    const target = itemsRef.current.find((i) => i.id === id);
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
    setList((prev) => prev.filter((i) => i.id !== id));
  };

  const moveItem = (id, dir) => {
    setList((prev) => {
      const i = prev.findIndex((x) => x.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const makeCover = (id) => {
    setList((prev) => {
      const i = prev.findIndex((x) => x.id === id);
      if (i <= 0) return prev;
      const next = [...prev];
      const [picked] = next.splice(i, 1);
      next.unshift(picked);
      return next;
    });
  };

  const addTextSlide = () => {
    const text = textDraft.trim();
    if (!text) return;
    if (itemsRef.current.length >= MAX_ITEMS) {
      setNotice(`You can add up to ${MAX_ITEMS} items per post.`);
      return;
    }
    setList((prev) => [...prev, { id: uid(), kind: 'text', type: 'text', text: text.slice(0, MAX_TEXT_SLIDE) }]);
    setTextDraft('');
    setShowTextBox(false);
    setNotice('');
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  /* ---------- Shop: products / services ---------- */
  const pickProductImage = (slot) => {
    pickingSlot.current = slot;
    if (prodFileRef.current) prodFileRef.current.click();
  };

  const onProductFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    const slot = pickingSlot.current;
    pickingSlot.current = null;
    if (!f || !slot) return;

    if (!(f.type || '').startsWith('image/')) { setNotice(`“${f.name}” isn’t an image.`); return; }
    if (f.size > MAX_PRODUCT_MB * 1024 * 1024) { setNotice(`“${f.name}” is larger than ${MAX_PRODUCT_MB} MB.`); return; }

    const small = await shrinkImage(f);
    const previewUrl = URL.createObjectURL(small);

    setShopList((prev) => {
      if (slot === 'new') {
        if (prev.length >= MAX_PRODUCTS) { URL.revokeObjectURL(previewUrl); return prev; }
        return [...prev, { id: uid(), kind: 'file', file: small, previewUrl, image: '', url: '', title: '' }];
      }
      return prev.map((p) => {
        if (p.id !== slot) return p;
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
        return { ...p, kind: 'file', file: small, previewUrl, image: '', uploadedName: undefined };
      });
    });
    setNotice('');
  };

  const updateProduct = (id, patch) => {
    setShopList((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  const removeProduct = (id) => {
    const target = shopRef.current.find((p) => p.id === id);
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
    setShopList((prev) => prev.filter((p) => p.id !== id));
  };

  const moveProduct = (id, dir) => {
    setShopList((prev) => {
      const i = prev.findIndex((x) => x.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  /* ---------- Save (create or update) ---------- */
  const submit = async (e) => {
    if (e) e.preventDefault();
    if (submitting) return;
    if (!user) { setNotice('You need to log in to post.'); return; }

    const text = caption.trim();
    if (!text && itemsRef.current.length === 0) {
      setNotice('Add a caption or at least one item first.');
      return;
    }

    // check the shop links before anything is uploaded
    const rows = shopRef.current;
    for (let i = 0; i < rows.length; i += 1) {
      if (!rows[i].url.trim()) { setNotice(`Product ${i + 1}: add the link where people can buy it.`); return; }
      if (!normalizeUrl(rows[i].url)) { setNotice(`Product ${i + 1}: that link doesn’t look valid.`); return; }
    }
    const siteRaw = website.trim();
    const site = siteRaw ? normalizeUrl(siteRaw) : '';
    if (siteRaw && !site) { setNotice('The brand website link doesn’t look valid.'); return; }

    setNotice('');
    setSubmitting(true);

    try {
      const isPrivate = license === 'Private';

      // STEP 1: upload new files to the Cloudflare R2 worker (already-uploaded ones are skipped on retry)
      const toUpload = itemsRef.current.filter((i) => i.kind === 'file' && !i.uploadedName);
      const toUploadProducts = shopRef.current.filter((p) => p.kind === 'file' && !p.uploadedName);
      const total = toUpload.length + toUploadProducts.length;
      setProgress({ done: 0, total });

      let done = 0;
      for (const it of toUpload) {
        // never send a private caption to the Worker's database
        const filename = await uploadToWorker(it.file, isPrivate ? '' : text, user.username, 'posts');
        setList((prev) => prev.map((p) => (p.id === it.id ? { ...p, uploadedName: filename } : p)));
        done += 1;
        setProgress({ done, total });
      }

      // product images go to their own R2 folder: social-media-assets/products/
      for (const p of toUploadProducts) {
        const filename = await uploadToWorker(p.file, '', user.username, 'products');
        setShopList((prev) => prev.map((x) => (x.id === p.id ? { ...x, uploadedName: filename } : x)));
        done += 1;
        setProgress({ done, total });
      }

      // STEP 2: build the ordered slide list (the first one is the cover)
      const mediaItems = itemsRef.current.map((it) => {
        if (it.kind === 'text') return { type: 'text', text: it.text };
        if (it.kind === 'existing') return { type: it.type, name: it.name };
        return { type: it.type, name: it.uploadedName };
      });
      const firstFile = mediaItems.find((m) => m.type !== 'text');

      const shopPayload = shopRef.current.map((p) => ({
        image: p.kind === 'file' ? p.uploadedName : p.image,
        url: normalizeUrl(p.url),
        title: p.title.trim().slice(0, 80),
      }));

      // STEP 3: encrypt private captions
      const key = isEdit ? (editRecord.privateKey || genKey()) : genKey();
      const payload = {
        content: isPrivate ? '[private]' : text,
        caption: isPrivate ? '' : text,
        encryptedContent: isPrivate ? await encryptText(key, text) : '',
        licenseType: license,
        media: firstFile ? firstFile.name : '',      // kept for older screens that read a single file
        mediaItems,
        bgColor: isHexColor(bgColor) ? bgColor.trim().toLowerCase() : '',
        products: shopPayload,
        brandWebsite: site || '',
      };
      if (firstFile) payload.mediaType = firstFile.type;
      else if (isEdit) payload.mediaType = '';

      if (!isEdit) {
        payload.author = user.id;
        payload.privateKey = key;
        payload.publicKey = genKey();
      } else if (!editRecord.privateKey) {
        payload.privateKey = key;
        payload.publicKey = genKey();
      }

      const url = isEdit
        ? `${PB_URL}/api/collections/posts/records/${editId}`
        : `${PB_URL}/api/collections/posts/records`;

      const res = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: pb.authStore.token,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          Object.keys(errorData).length ? JSON.stringify(errorData) : `PocketBase rejection status: ${res.status}`
        );
      }

      const saved = await res.json();
      const missing = [];
      if (!('mediaItems' in saved)) missing.push('mediaItems (JSON)');
      if (!('bgColor' in saved)) missing.push('bgColor (Text)');
      if (shopPayload.length && !('products' in saved)) missing.push('products (JSON)');
      if (site && !('brandWebsite' in saved)) missing.push('brandWebsite (Text)');
      if (missing.length) {
        alert(
          `Saved, but your "posts" collection has no ${missing.join(' and ')} field, so ` +
          'that part was not stored. Add the field(s) in PocketBase and save again.'
        );
      }

      nav('/');
    } catch (err) {
      console.error('Save failed', err);
      setNotice(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  };

  const removePost = async () => {
    if (!isEdit) return;
    if (!window.confirm("Delete this post permanently? This can't be undone.")) return;
    try {
      await pb.collection('posts').delete(editId, { $autoCancel: false });
      nav('/');
    } catch (err) {
      console.error('Delete failed', err);
      setNotice('Could not delete this post. Please try again.');
    }
  };

  /* ---------- Render ---------- */
  const submitLabel = submitting
    ? (progress && progress.total > 0 ? `Uploading ${Math.min(progress.done + 1, progress.total)} of ${progress.total}…` : 'Saving…')
    : (isEdit ? 'Save changes' : 'Post');

  const colorLabel = bgColor
    ? (POST_COLORS.find((c) => c.value === bgColor)?.name || `Custom ${bgColor}`)
    : 'Default';

  // what to tell the creator about the website field
  let siteHint = 'Optional. People will see a website icon after the last product.';
  let siteHintGood = false;
  if (brand.status === 'loading') {
    siteHint = 'Checking your brand profile…';
  } else if (brand.status === 'found' && brand.website && website.trim() === brand.website) {
    siteHint = '✓ Filled in from your brand profile.';
    siteHintGood = true;
  } else if (brand.status === 'found' && !brand.website) {
    siteHint = 'Your brand profile has no website yet. Enter it here for this post.';
  } else if (brand.status === 'found' && brand.website) {
    siteHint = 'Using a different link for this post than your brand profile.';
  }

  const header = (
    <header className="mf-cp-top">
      <div className="mf-cp-top-inner">
        <Link to="/" className="mf-cp-iconbtn" aria-label="Back to feed" title="Back to feed">
          <ArrowBackRoundedIcon />
        </Link>
        <h1 className="mf-cp-title">{editId ? 'Edit post' : 'Create post'}</h1>
        <div className="mf-cp-top-actions">
          <button
            type="button"
            className="mf-cp-iconbtn"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
          </button>
          <button
            type="submit"
            form="mf-cp-form"
            className="mf-btn mf-btn--primary"
            disabled={submitting || (!!editId && !isEdit)}
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </header>
  );

  if (editId && (loadingEdit || editError || !user)) {
    return (
      <div className="page create mf-feed mf-cp" data-mf-theme={theme}>
        {header}
        <div className="mf-cp-state">
          {loadingEdit && <p>Loading your post…</p>}
          {!loadingEdit && !user && <p>Please log in to edit this post.</p>}
          {!loadingEdit && user && editError && (
            <>
              <p>{editError}</p>
              <Link to="/" className="mf-btn">Back to feed</Link>
            </>
          )}
        </div>
        <StickyFooter />
      </div>
    );
  }

  return (
    <div className="page create mf-feed mf-cp" data-mf-theme={theme}>
      {header}

      <form id="mf-cp-form" className="mf-cp-grid" onSubmit={submit}>
        {/* ---------------- Left: content ---------------- */}
        <div className="mf-cp-col">
          <section className="mf-cp-card">
            <h2 className="mf-cp-h">Caption</h2>
            <textarea
              className="mf-textarea mf-cp-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') submit(); }}
              placeholder="What’s on your mind?"
              maxLength={2000}
              aria-label="Caption"
            />
            <div className="mf-cp-count">{caption.length}/2000</div>
          </section>

          <section className="mf-cp-card">
            <h2 className="mf-cp-h">
              <span>Content</span>
              <small>{items.length}/{MAX_ITEMS} items</small>
            </h2>

            <div
              className={`mf-cp-drop${dragOver ? ' is-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
            >
              <div className="mf-cp-drop-ico" aria-hidden="true"><CloudUploadRoundedIcon /></div>
              <p>Drop images, videos or audio here</p>
              <small>Mix as many as you like · up to {MAX_FILE_MB} MB each</small>
              <div className="mf-cp-drop-actions">
                <button type="button" className="mf-btn mf-btn--sm" onClick={() => fileInputRef.current?.click()}>
                  <CloudUploadRoundedIcon style={{ fontSize: 18 }} /> Add files
                </button>
                <button type="button" className="mf-btn mf-btn--sm" onClick={() => setShowTextBox((v) => !v)}>
                  <TextFieldsRoundedIcon style={{ fontSize: 18 }} /> Add text slide
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,audio/*"
                hidden
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              />
            </div>

            {showTextBox && (
              <div className="mf-cp-textbox">
                <textarea
                  className="mf-textarea"
                  autoFocus
                  value={textDraft}
                  onChange={(e) => setTextDraft(e.target.value)}
                  placeholder="Write something for this slide…"
                  maxLength={MAX_TEXT_SLIDE}
                  aria-label="Text slide"
                />
                <div className="mf-cp-textbox-row">
                  <span className="mf-cp-count" style={{ marginRight: 'auto' }}>{textDraft.length}/{MAX_TEXT_SLIDE}</span>
                  <button type="button" className="mf-btn mf-btn--sm mf-btn--ghost" onClick={() => { setShowTextBox(false); setTextDraft(''); }}>
                    Cancel
                  </button>
                  <button type="button" className="mf-btn mf-btn--sm mf-btn--primary" onClick={addTextSlide} disabled={!textDraft.trim()}>
                    Add slide
                  </button>
                </div>
              </div>
            )}

            {items.length > 0 && (
              <>
                <ul className="mf-cp-tiles">
                  {items.map((it, i) => {
                    const Icon = TYPE_ICON[it.type] || ImageRoundedIcon;
                    return (
                      <li key={it.id} className={`mf-cp-tile${i === 0 ? ' is-cover' : ''}`}>
                        <div className="mf-cp-thumb"><Thumb item={it} record={editRecord} /></div>
                        <span className="mf-cp-num">{i + 1}</span>
                        {i === 0 && <span className="mf-cp-cover">COVER</span>}
                        <span className="mf-cp-type" title={TYPE_LABEL[it.type]}><Icon /></span>
                        <div className="mf-cp-tools">
                          <button
                            type="button"
                            className={i === 0 ? 'is-on' : ''}
                            onClick={() => makeCover(it.id)}
                            disabled={i === 0}
                            aria-label={i === 0 ? 'This is the cover' : 'Make cover'}
                            title={i === 0 ? 'This is the cover' : 'Make cover'}
                          >
                            {i === 0 ? <StarRoundedIcon /> : <StarBorderRoundedIcon />}
                          </button>
                          <button
                            type="button"
                            onClick={() => moveItem(it.id, -1)}
                            disabled={i === 0}
                            aria-label="Move earlier"
                            title="Move earlier"
                          >
                            <ChevronLeftRoundedIcon />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveItem(it.id, 1)}
                            disabled={i === items.length - 1}
                            aria-label="Move later"
                            title="Move later"
                          >
                            <ChevronRightRoundedIcon />
                          </button>
                          <button type="button" onClick={() => removeItem(it.id)} aria-label="Remove" title="Remove">
                            <CloseRoundedIcon />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <p className="mf-cp-tip">
                  The cover is shown first; the rest follow in number order. Tap ★ to make an item the cover.
                </p>
              </>
            )}
          </section>

          {/* ---------- Shop links: products / services + brand website ---------- */}
          <section className="mf-cp-card">
            <h2 className="mf-cp-h">
              <span>Shop links</span>
              <small>{shop.length}/{MAX_PRODUCTS} products</small>
            </h2>
            <p className="mf-cp-hint">
              Attach up to {MAX_PRODUCTS} products or services to this post. Viewers see them as small images under the
              comments button and can swipe through them or tap one to open its link.
            </p>

            <label className="mf-cp-label" htmlFor="mf-cp-website">Brand website</label>
            <div className="mf-cp-field">
              <LanguageRoundedIcon />
              <input
                id="mf-cp-website"
                className="mf-input"
                type="text"
                inputMode="url"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={website}
                onChange={(e) => { websiteTouched.current = true; setWebsite(e.target.value); }}
                onKeyDown={noEnterSubmit}
                placeholder="https://yourbrand.com"
                maxLength={300}
              />
            </div>
            <p className={`mf-cp-fieldhint${siteHintGood ? ' is-good' : ''}`}>{siteHint}</p>

            {shop.length === 0 ? (
              <div className="mf-cp-empty">
                <ShoppingBagRoundedIcon />
                <span>No products yet. Add an image and the link where people can buy it.</span>
              </div>
            ) : (
              <ul className="mf-cp-shop">
                {shop.map((p, i) => (
                  <li key={p.id} className="mf-cp-prod">
                    <button
                      type="button"
                      className="mf-cp-prodimg"
                      onClick={() => pickProductImage(p.id)}
                      aria-label={`Change the image of product ${i + 1}`}
                      title="Change image"
                    >
                      <ProductImg p={p} />
                      <i>{i + 1}</i>
                      <span>Change</span>
                    </button>

                    <div className="mf-cp-prodfields">
                      <input
                        className="mf-input"
                        type="text"
                        inputMode="url"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        value={p.url}
                        onChange={(e) => updateProduct(p.id, { url: e.target.value })}
                        onKeyDown={noEnterSubmit}
                        placeholder="Buying link (https://…)"
                        aria-label={`Buying link for product ${i + 1}`}
                        maxLength={600}
                      />
                      <input
                        className="mf-input"
                        type="text"
                        value={p.title}
                        onChange={(e) => updateProduct(p.id, { title: e.target.value })}
                        onKeyDown={noEnterSubmit}
                        placeholder="Name (optional)"
                        aria-label={`Name of product ${i + 1}`}
                        maxLength={80}
                      />
                      <div className="mf-cp-prodtools">
                        <button
                          type="button"
                          className="mf-cp-iconbtn"
                          onClick={() => moveProduct(p.id, -1)}
                          disabled={i === 0}
                          aria-label="Move up"
                          title="Show earlier"
                        >
                          <KeyboardArrowUpRoundedIcon />
                        </button>
                        <button
                          type="button"
                          className="mf-cp-iconbtn"
                          onClick={() => moveProduct(p.id, 1)}
                          disabled={i === shop.length - 1}
                          aria-label="Move down"
                          title="Show later"
                        >
                          <KeyboardArrowDownRoundedIcon />
                        </button>
                        <button
                          type="button"
                          className="mf-cp-iconbtn is-danger"
                          onClick={() => removeProduct(p.id)}
                          aria-label={`Remove product ${i + 1}`}
                          title="Remove"
                          style={{ marginLeft: 'auto' }}
                        >
                          <DeleteOutlineRoundedIcon />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {shop.length < MAX_PRODUCTS && (
              <button type="button" className="mf-btn mf-btn--sm mf-cp-addprod" onClick={() => pickProductImage('new')}>
                <AddPhotoAlternateRoundedIcon style={{ fontSize: 18 }} /> Add product image
              </button>
            )}

            <input
              ref={prodFileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onProductFile}
            />
          </section>
        </div>

        {/* ---------------- Right: settings ---------------- */}
        <aside className="mf-cp-col">
          <section className="mf-cp-card">
            <h2 className="mf-cp-h">Visibility</h2>
            <div className="mf-cp-seg" role="radiogroup" aria-label="Visibility">
              <button
                type="button"
                role="radio"
                aria-checked={license === 'Public'}
                className={license === 'Public' ? 'is-on' : ''}
                onClick={() => setLicense('Public')}
              >
                <PublicRoundedIcon />
                <span><b>Public</b><small>Everyone can see it</small></span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={license === 'Private'}
                className={license === 'Private' ? 'is-on' : ''}
                onClick={() => setLicense('Private')}
              >
                <LockRoundedIcon />
                <span><b>Private</b><small>Encrypted caption, hidden from others</small></span>
              </button>
            </div>
          </section>

          <section className="mf-cp-card">
            <h2 className="mf-cp-h">
              <span>Card background</span>
              <small>{colorLabel}</small>
            </h2>
            <div className="mf-cp-swatches" role="radiogroup" aria-label="Post background color">
              {POST_COLORS.map((c) => {
                const on = (bgColor || '') === c.value;
                const checkColor = c.value ? cardColorVars(c.value)['--mf-text'] : undefined;
                return (
                  <button
                    key={c.name}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    aria-label={c.name}
                    title={c.name}
                    className={`mf-cp-sw${c.value ? '' : ' mf-cp-sw--default'}${on ? ' is-on' : ''}`}
                    style={c.value ? { background: c.value } : undefined}
                    onClick={() => setBgColor(c.value)}
                  >
                    {on && <CheckRoundedIcon style={checkColor ? { color: checkColor } : undefined} />}
                  </button>
                );
              })}
              <label
                className={`mf-cp-sw mf-cp-sw--custom${bgColor && !POST_COLORS.some((c) => c.value === bgColor) ? ' is-on' : ''}`}
                title="Pick any color"
              >
                <input
                  type="color"
                  value={isHexColor(bgColor) ? bgColor : '#6d28d9'}
                  onChange={(e) => setBgColor(e.target.value.toLowerCase())}
                  aria-label="Custom background color"
                />
              </label>
            </div>
          </section>

          <section className="mf-cp-card">
            <h2 className="mf-cp-h">Preview</h2>
            <PostPreview
              items={items}
              caption={caption}
              bgColor={bgColor}
              license={license}
              user={user}
              editRecord={editRecord}
              shopCount={shop.length}
              hasSite={!!normalizeUrl(website)}
            />
          </section>

          <section className="mf-cp-card mf-cp-publish">
            {notice && <div className="mf-cp-notice" role="alert">{notice}</div>}

            {submitting && progress && progress.total > 0 && (
              <div
                className="mf-cp-progress"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={progress.total}
                aria-valuenow={progress.done}
              >
                <span style={{ width: `${(progress.done / progress.total) * 100}%` }} />
              </div>
            )}

            <button type="submit" className="mf-btn mf-btn--primary" disabled={submitting}>
              {submitLabel}
            </button>

            {isEdit && (
              <button type="button" className="mf-btn mf-btn--danger" onClick={removePost} disabled={submitting}>
                <DeleteOutlineRoundedIcon style={{ fontSize: 20 }} /> Delete post
              </button>
            )}
          </section>
        </aside>
      </form>

      <StickyFooter />
    </div>
  );
}